import { useEffect, useState } from 'react';
import {
  applyTheme,
  readPreference,
  subscribePreferenceChange,
  writePreference,
  type ThemeMode,
  type ThemePreference,
} from '../theme';

const MODES: { mode: ThemeMode; label: string; icon: React.ReactNode }[] = [
  { mode: 'light', label: 'Light theme', icon: <SunIcon /> },
  { mode: 'dark', label: 'Dark theme', icon: <MoonIcon /> },
  { mode: 'system', label: 'Follow the system theme', icon: <SystemIcon /> },
];

/**
 * Light · Dark · System, one click each, in the header. Writes the shared
 * preference (see theme.ts) and keeps the transcript event-color setting the
 * embedded viewers' own picker also edits.
 */
export function ThemePicker() {
  const [pref, setPref] = useState<ThemePreference>(() => readPreference());
  // Another tab (or a viewer's picker) may change the stored preference.
  useEffect(
    () => subscribePreferenceChange(() => setPref(readPreference())),
    []
  );

  const choose = (mode: ThemeMode) => {
    const next = { ...readPreference(), mode };
    writePreference(next);
    setPref(next);
    applyTheme();
  };

  return (
    <div className="seg-on-header shrink-0" role="group" aria-label="Theme">
      {MODES.map(m => (
        <button
          key={m.mode}
          type="button"
          aria-pressed={pref.mode === m.mode}
          aria-label={m.label}
          data-tip={m.label}
          className="tip"
          onClick={() => choose(m.mode)}
        >
          {m.icon}
        </button>
      ))}
    </div>
  );
}

const iconProps = {
  width: 14,
  height: 14,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

function SunIcon() {
  return (
    <svg {...iconProps}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2m0 16v2M2 12h2m16 0h2M4.93 4.93l1.42 1.42m11.3 11.3 1.42 1.42m0-14.14-1.42 1.42m-11.3 11.3-1.42 1.42" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg {...iconProps}>
      <path d="M20.9 13.2A9 9 0 0 1 10.8 3.1 9 9 0 1 0 20.9 13.2Z" />
    </svg>
  );
}

function SystemIcon() {
  return (
    <svg {...iconProps}>
      <rect x="3" y="4" width="18" height="12" rx="2" />
      <path d="M8 20h8M12 16v4" />
    </svg>
  );
}
